import React, { useEffect, useId, useRef, useState } from 'react';
import { Clock, Laptop, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Smartphone } from 'lucide-react';
import { LoginResponse } from '../types';
import { canRequestHere, suggestDeviceLabel } from '../services/deviceIdentity';
import { formatVerifyCode } from '../utils/deviceProof';

/**
 * 기기 신뢰 — 로그인 화면의 기기 상태 (서버 server/services/deviceTrust.js 판정 결과를 그린다)
 *
 * 비밀번호는 맞았고 기기가 막힌 경우만 온다(DEVICE_* · APP_UPDATE_REQUIRED). 판정은 언제나 서버이고
 * 이 화면은 안내만 한다 — 예를 들어 PC 브라우저에 요청 버튼을 숨기는 것도 안내일 뿐이다.
 *
 * 대기 화면은 보이는 동안 20초마다(최대 15분) 다시 로그인해 승인을 감지한다. 자격증명은 Login의
 * 메모리에만 있고 저장하지 않는다.
 */

export type DeviceGateKind = 'not_registered' | 'pending' | 'blocked' | 'update' | 'required';

export function deviceGateKindOf(code?: string): DeviceGateKind | null {
  switch (code) {
    case 'DEVICE_NOT_REGISTERED': return 'not_registered';
    case 'DEVICE_PENDING': return 'pending';
    case 'DEVICE_BLOCKED': return 'blocked';
    case 'APP_UPDATE_REQUIRED': return 'update';
    case 'DEVICE_REQUIRED': return 'required';
    default: return null;
  }
}

interface DeviceGateScreenProps {
  kind: DeviceGateKind;
  response: LoginResponse;
  busy: boolean;
  /** 이 기기 승인 요청(기기 이름) */
  onRequest: (label: string) => void;
  /** 승인 여부 다시 확인(같은 자격증명으로 재로그인) */
  onCheckAgain: () => void;
  /** 로그인 폼으로 돌아가기 */
  onBack: () => void;
}

const POLL_MS = 20000;
const POLL_MAX_MS = 15 * 60 * 1000;

export const DeviceGateScreen: React.FC<DeviceGateScreenProps> = ({ kind, response, busy, onRequest, onCheckAgain, onBack }) => {
  const fid = useId();
  const [label, setLabel] = useState(() => suggestDeviceLabel());
  const startedAt = useRef(Date.now());

  // 대기 화면: 보이는 동안만 주기적으로 승인 여부를 확인한다
  useEffect(() => {
    if (kind !== 'pending') return undefined;
    startedAt.current = Date.now();
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible' || busy) return;
      if (Date.now() - startedAt.current > POLL_MAX_MS) return;
      onCheckAgain();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [kind, busy, onCheckAgain]);

  const backButton = (
    <button
      type="button"
      onClick={onBack}
      className="w-full text-sm text-slate-500 hover:text-slate-700 py-2"
    >
      다른 계정으로 로그인
    </button>
  );

  if (kind === 'pending') {
    const device = response.device;
    return (
      <div className="p-8 space-y-5" aria-live="polite">
        <Header icon={<Clock className="h-6 w-6 text-slate-600" />} title="기기 승인 대기 중" />
        <p className="text-sm text-slate-600 leading-relaxed">
          이 기기는 관리자 승인을 기다리고 있습니다. 아래 확인 코드로 본인임을 확인하면 승인됩니다.
        </p>
        {device?.code && !device.selfConfirmed && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
            <div className="text-xs text-slate-500 mb-1">확인 코드</div>
            <div className="text-3xl font-bold tracking-widest text-slate-900 tabular-nums">{formatVerifyCode(device.code)}</div>
            <div className="text-xs text-slate-500 mt-2">기기 지문 {device.fingerprint}</div>
          </div>
        )}
        {device?.selfConfirmed ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            본인 확인이 끝났습니다. 관리자가 승인하면 바로 들어갈 수 있습니다.
          </p>
        ) : (
          <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
            <li>이미 쓰고 있는 <b>PC 앱</b>에서 <b>설정 → 내 기기</b>를 열고 이 코드를 입력하세요.</li>
            <li>PC 앱이 없으면 관리자에게 연락해 이 코드를 직접 알려 주세요.</li>
          </ol>
        )}
        <p className="text-xs text-slate-500">
          내가 요청하지 않았는데 이 화면이 보인다면, 비밀번호가 새어 나갔을 수 있습니다. 인트라넷 비밀번호를 바로 바꾸세요.
        </p>
        <button
          type="button"
          onClick={onCheckAgain}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-70"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          승인 확인
        </button>
        {backButton}
      </div>
    );
  }

  if (kind === 'not_registered') {
    const where = canRequestHere();
    if (where.ok && response.canRequest !== false) {
      return (
        <form
          className="p-8 space-y-5"
          onSubmit={(e) => { e.preventDefault(); if (label.trim()) onRequest(label.trim()); }}
        >
          <Header icon={<Smartphone className="h-6 w-6 text-slate-600" />} title="등록되지 않은 기기" />
          <p className="text-sm text-slate-600 leading-relaxed">
            모바일에서는 관리자가 승인한 기기에서만 INA-TALK를 쓸 수 있습니다. 이 기기의 승인을 요청할까요?
          </p>
          <div>
            <label htmlFor={`${fid}-label`} className="block text-sm font-medium text-slate-700 mb-1">기기 이름</label>
            <input
              id={`${fid}-label`}
              value={label}
              maxLength={40}
              onChange={(e) => setLabel(e.target.value)}
              className="block w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-400 focus:border-brand-500"
              placeholder="예: 내 아이폰"
              required
            />
            <p className="text-xs text-slate-500 mt-1">관리자가 승인 목록에서 이 이름을 봅니다.</p>
          </div>
          <button
            type="submit"
            disabled={busy || !label.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-70"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            승인 요청
          </button>
          {backButton}
        </form>
      );
    }
    const reason = 'reason' in where ? where.reason : 'desktop-browser';
    return (
      <div className="p-8 space-y-5">
        {reason === 'ios-browser' ? (
          <>
            <Header icon={<Smartphone className="h-6 w-6 text-slate-600" />} title="홈 화면 앱에서 요청해 주세요" />
            <p className="text-sm text-slate-600 leading-relaxed">
              아이폰·아이패드는 Safari와 홈 화면 앱이 따로 저장되어, 여기서 승인받아도 앱에서는 쓸 수 없습니다.
              Safari의 공유 버튼 → <b>홈 화면에 추가</b>로 INA-TALK를 설치한 뒤, 그 앱에서 다시 로그인해 주세요.
            </p>
          </>
        ) : reason === 'unsupported' ? (
          <>
            <Header icon={<ShieldAlert className="h-6 w-6 text-slate-600" />} title="이 브라우저에서는 등록할 수 없습니다" />
            <p className="text-sm text-slate-600 leading-relaxed">
              개인정보 보호(시크릿) 모드이거나 기기 키를 저장할 수 없는 브라우저입니다. 일반 모드의 Chrome·Safari에서 다시 시도해 주세요.
            </p>
          </>
        ) : (
          <>
            <Header icon={<Laptop className="h-6 w-6 text-slate-600" />} title="PC에서는 PC 앱을 이용해 주세요" />
            <p className="text-sm text-slate-600 leading-relaxed">
              웹 브라우저로는 INA-TALK에 접속할 수 없습니다. PC 앱 설치 파일은 <b>인트라넷 자료실</b>에서 내려받을 수 있습니다.
            </p>
          </>
        )}
        {backButton}
      </div>
    );
  }

  if (kind === 'update') {
    return (
      <div className="p-8 space-y-5">
        <Header icon={<RefreshCw className="h-6 w-6 text-slate-600" />} title="PC 앱 업데이트가 필요합니다" />
        <p className="text-sm text-slate-600 leading-relaxed">
          보안 정책이 바뀌어 지금 버전으로는 로그인할 수 없습니다. 화면 위에 뜨는 업데이트 안내에서 <b>지금 설치</b>를 눌러 주세요.
          안내가 보이지 않으면 인트라넷 자료실에서 최신 설치 파일을 받아 설치해 주세요.
        </p>
        {backButton}
      </div>
    );
  }

  if (kind === 'required') {
    return (
      <div className="p-8 space-y-5">
        <Header icon={<RefreshCw className="h-6 w-6 text-slate-600" />} title="앱을 새로 고쳐 주세요" />
        <p className="text-sm text-slate-600 leading-relaxed">{response.message || '앱을 새로 고친 뒤 다시 로그인해 주세요.'}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full py-3 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
        >
          새로 고침
        </button>
        {backButton}
      </div>
    );
  }

  // blocked
  return (
    <div className="p-8 space-y-5">
      <Header icon={<ShieldCheck className="h-6 w-6 text-slate-600" />} title="사용이 허용되지 않은 기기" />
      <p className="text-sm text-slate-600 leading-relaxed">
        {response.message || '이 기기는 사용이 허용되지 않았습니다. 관리자에게 문의해 주세요.'}
      </p>
      {response.reason && (
        <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">사유: {response.reason}</p>
      )}
      {backButton}
    </div>
  );
};

const Header: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-3">
    <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">{icon}</div>
    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
  </div>
);
